/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of, race } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { ChangeLogEntry, Procedure } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    getPersistedChangesData,
    getRevertedChangesForModel,
    getRevertedCreateIds,
    reconcileInstancesWithChanges,
} from '@mona/pdms/data-access-changelog';
import { currentEncounterId, isEmptyEncounterId, withCurrentEncounterId } from '@mona/pdms/data-access-combined';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { selectCurrentRoute, selectRouteData } from '@mona/store';
import { ProceduresApi } from '../../infrastructure';
import { ProceduresActions } from '../actions';
import { addProceduresChanges } from '../actions/procedure.actions';
import { selectProcedureAll } from '../selectors';

/**
 * ProceduresEffects
 */
@Injectable()
export class ProceduresEffects {
    changeLogMap$ = this.store.select(ChangeLogSelectors.getChangesMap);
    procedurePrescriptionAll$ = this.store.select(selectProcedureAll);

    /** Load Procedures */
    loadProcedures$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProceduresActions.loadProcedures),
            withCurrentEncounterId(),
            concatMap(([, encounterId]) =>
                race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() => ProceduresActions.loadProceduresCancelled({ reason: 'encounter-cleared' })),
                    ),
                    this.proceduresApi.getProcedures(encounterId).pipe(
                        isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                        withLatestFrom(this.changeLogMap$),
                        map(([data, changesMap]) => {
                            const changes = changesMap['Procedure'] || [];

                            if (changes.length) {
                                data = applyInstancesChanges(data, changes) as Procedure[];
                            }

                            return ProceduresActions.loadProceduresSuccess({
                                procedures: data,
                            });
                        }),
                        catchError(error => of(ProceduresActions.loadProceduresFailure({ error }))),
                        defaultIfEmpty(
                            ProceduresActions.loadProceduresCancelled({ reason: 'encounter-changed' }) as Action,
                        ),
                    ),
                ),
            ),
        ),
    );

    // On savechange action toggle loading to avoid multiple documentations on slow network
    onSaveChangeActionToggleLoading$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.saveChangeAction.action),
                withLatestFrom(this.store.select(selectCurrentRoute)),
                filter(
                    ([action, route]) =>
                        route && route.url?.includes('procedures') && action.change?.model === 'Procedure',
                ),
                tap(() => this.store.dispatch(ProceduresActions.setIsLoadingAction({ isLoading: true }))),
            ),
        { dispatch: false },
    );

    /** Handle save failures - disable loading state */
    onChangeSaveFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.saveChangeAction.failedAction),
                withLatestFrom(this.store.select(selectRouteData)),
                filter(([, routeData]) => routeData && routeData['path'].includes('procedures')),
                tap(() => this.store.dispatch(ProceduresActions.setIsLoadingAction({ isLoading: false }))),
            ),
        { dispatch: false },
    );

    /** Listen change save success & upsert entity + show message */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.action,
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.action,
                    ChangeLogAction.saveChangesAction.succeededAction,
                    ChangeLogAction.revertOptimisticChange,
                    ChangeLogAction.revertOptimisticChanges,
                    ChangeLogAction.revertStaleSavingEntries,
                ),
                withLatestFrom(
                    this.procedurePrescriptionAll$,
                    this.changeLogMap$,
                    this.store.select(selectCurrentRoute),
                ),
                tap(([action, data, changesMap, route]) => {
                    const changes: ChangeLogEntry<Procedure>[] = changesMap['Procedure'] || [];
                    const reverted = getRevertedChangesForModel(action, 'Procedure');
                    let procedures = reconcileInstancesWithChanges(data, changes, reverted);

                    // A reverted optimistic create is filtered out of the reconciled array, but
                    // `upsertMany` never removes entities — so the phantom row would linger with
                    // its `isSaving` flag set. Explicitly remove reverted create ids (mirrors the
                    // entity-adapter effects, e.g. AdditionalDevices).
                    const revertedCreateIds = getRevertedCreateIds(reverted);
                    if (revertedCreateIds.length) {
                        this.store.dispatch(ProceduresActions.deleteProcedures({ ids: revertedCreateIds }));
                    }

                    if (procedures) {
                        // To add temporary lastChangedAt for procedure changes
                        procedures = procedures.map(procedure => {
                            if (procedure.id === changes[changes.length - 1]?.modelId) {
                                return { ...procedure, lastChangedAt: new Date() };
                            }
                            return procedure;
                        });

                        this.store.dispatch(
                            ProceduresActions.upsertProcedures({
                                procedures,
                            }),
                        );
                    }
                    // check if route is procedures
                    if (route.url?.includes('procedures')) {
                        this.store.dispatch(ProceduresActions.setIsLoadingAction({ isLoading: false }));
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangePersisted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(this.procedurePrescriptionAll$, this.changeLogMap$),
                tap(([, data, changesMap]) => {
                    const changes = changesMap['Procedure'] || [];

                    if (changes.length) {
                        const { toRemove, toUpdate } = getPersistedChangesData(data, changes);

                        this.store.dispatch(
                            addProceduresChanges({
                                toUpdateEntities: toUpdate || [],
                                toRemoveIds: toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     * @param actions$ Actions
     * @param store
     * @param proceduresApi ProceduresApi
     */
    constructor(
        private actions$: Actions,
        private store: Store<any>,
        private proceduresApi: ProceduresApi,
    ) {}
}
